import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { classificationInterface } from 'src/app/util/interfaces/classification';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class RegisterRequestCustomfieldService {
	private registerRequestCustomfieldUrl = environment.baseApiUrl + 'register/requestCustomfield';
	private registerRequestCustomfieldClassificationUrl = environment.baseApiUrl + 'register/requestCustomfield/classification';
	private registerRequestCustomfieldSubfieldUrl = environment.baseApiUrl + 'register/requestCustomfield/subfield';
	private registerRequestCustomfieldSortOrderUrl = environment.baseApiUrl + 'register/requestCustomfield/sort-order';
	private getAnalystsApproversUrl = environment.baseApiUrl + 'register/requestCustomfield/analystsApprovers';

	private headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService
	) {}

	getCustomfields(name: string, idCategory: string, idTopic: string, newer: boolean): Observable<genericFormInterface[]> {
		return this.http
			.get<any[]>(this.registerRequestCustomfieldUrl, {
				headers: this.headers,
				params: {
					name: name,
					idCategory: idCategory,
					idTopic: idTopic,
					newer: newer,
				},
			})
			.pipe(
				map((customfieldArrRes) => {
					const newCustomfieldArray: genericFormInterface[] = [];
					for (const customfieldRes of customfieldArrRes) {
						const newCustomfield: genericFormInterface = {
							id: customfieldRes.id,
							name: customfieldRes.name,
							label: customfieldRes.name,
							inputType: customfieldRes.inputType,
							required: customfieldRes.required,
							disabled: customfieldRes.active == 0,
							hidden: customfieldRes.hidden_field,
							analystView: customfieldRes.analyst_view,
							relatedTask: customfieldRes.related_task,
							items: [],
							multiInputLineId: [],
							values: [],
						};

						if (customfieldRes.items?.length > 0) {
							for (const item of customfieldRes.items) {
								const newItem: genericFormInterface = {
									id: item.id,
									name: item.text_item,
									label: item.text_item,
									values: [item.value_item],
									subfield_items: item.related_request_field,
								};
								newCustomfield.items.push(newItem);
							}
						}

						newCustomfieldArray.push(newCustomfield);
					}

					return newCustomfieldArray;
				})
			);
	}

	createCustomfield(customfield: genericFormInterface, classification: classificationInterface[], task: string): Observable<string[]> {
		const body = this.createCustomfieldCrudBody(customfield, classification, task);

		return this.http.post<any>(this.registerRequestCustomfieldUrl, body, { headers: this.headers, params: {} }).pipe(
			map((res) => {
				return res.data;
			})
		);
	}

	updateCustomfield(customfield: genericFormInterface, classification: classificationInterface[], task: string): Observable<string[]> {
		const body = this.createCustomfieldCrudBody(customfield, classification, task);

		return this.http.put<any>(this.registerRequestCustomfieldUrl, body, { headers: this.headers, params: {} });
	}

	deleteCustomfield(id: string): Observable<any> {
		return this.http.delete(this.registerRequestCustomfieldUrl, { headers: this.headers, params: { id: id } });
	}

	getCustomfieldClassification(id: string): Observable<classificationInterface[]> {
		return this.http.get<classificationInterface[]>(this.registerRequestCustomfieldClassificationUrl, {
			headers: this.headers,
			params: { id: id },
		});
	}

	getCustomfieldSubfield(id: string): Observable<{ id: number; name: string }[]> {
		return this.http.get<{ id: number; name: string }[]>(this.registerRequestCustomfieldSubfieldUrl, {
			headers: this.headers,
			params: { id: id },
		});
	}

	getAnalystsApprovers(): Observable<any> {
		return this.http.get<{ text_item: string; value_item: string }[]>(this.getAnalystsApproversUrl, { headers: this.headers });
	}

	getCustomfieldSortOrder(customer: string, category: string, topic: string): Observable<any[]> {
		return this.http.get<any[]>(this.registerRequestCustomfieldSortOrderUrl, {
			headers: this.headers,
			params: { customer, category, topic },
		});
	}

	updateCustomfieldSortOrder(
		customer: string,
		category: string,
		topic: string,
		fields: { request_field: number; sort_order: number }[]
	): Observable<any> {
		return this.http.put(this.registerRequestCustomfieldSortOrderUrl, { customer, category, topic, fields }, { headers: this.headers });
	}

	createCustomfieldCrudBody(customfield: genericFormInterface, classification: classificationInterface[], task: string): object {
		const body = {
			id: customfield.id,
			field_name: customfield.name,
			field_type: customfield.inputType,
			required: customfield.required == true ? 1 : 0,
			hidden_field: customfield.hidden == true ? 1 : 0,
			analyst_view: customfield.analystView == true ? 1 : 0,
			active: !customfield.disabled == true ? 1 : 0,
			sort_order: 0,
			items: [],
			classification: classification,
			task: task,
		};

		if (
			body.field_type == 'Select' ||
			body.field_type == 'Multi-Select' ||
			body.field_type == 'Radio' ||
			body.field_type == 'Checkbox'
		) {
			const cfItems = [];
			for (const [index, selectItem] of customfield.items[0].values.entries()) {
				const temporaryValue = [
					selectItem[0],
					selectItem[1] && selectItem[1].length > 0
						? selectItem[1]
								.map((v) => {
									return v.values[0];
								})
								.join(',')
						: '',
				];
				cfItems.push({ id: customfield.items[0].multiInputLineId[index], value: temporaryValue });
			}
			body.items = cfItems;
		}

		return body;
	}
}
